<style scoped>
.overview-container {
  display: block;
  align-items: center;
}
.overview-container div {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  max-width: 500px;
  margin: 8px auto;
  align-self: center;
  text-align: right;
}
@media only screen and (min-width: 1264px) {
  .overview-container {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    font-size: 18px;
  }
}
</style>
<template>
  <div>
    <v-data-table
      :headers="headers"
      :hide-default-header="!isValidationIssues"
      :items="invalidActivities"
      :items-per-page="15"
      :mobile-breakpoint="dataTableMobileBreakpoint"
      class="elevation-1"
      no-data-text="No Invalid Activities"
    >
      <template v-slot:top>
        <div class="d-flex pa-4">
          <span class="headline primary--text font-weight-medium">
            <v-icon class="primary--text" left
              >mdi-check-box-multiple-outline</v-icon
            >Validation Report
          </span>
          <v-spacer></v-spacer>
          <excel-download-button
            class="mr-2"
            :size="headerButtonSize"
            :onClick="exportAsExcel"
            :isLoading="isExportingExcel"
          />
        </div>
        <v-card flat class="d-flex pa-4 pa-0 text-left">
          <div class="col-md-6 pa-0">
            <div>
              <v-icon v-if="voyageStartTimeValid" large color="success">mdi-check-circle</v-icon>
              <v-icon v-else large color="error">mdi-close-circle</v-icon>
              <span class="font-weight-bold ml-1">Voyage Start/End Time Valid</span>
            </div>
            <div>
              <v-icon v-if="noActivityTimeGaps" large color="success">mdi-check-circle</v-icon>
              <v-icon v-else large color="error">mdi-close-circle</v-icon>
              <span class="font-weight-bold ml-1">No Time Gaps or Overlaps</span>
            </div>
            <div>
              <v-icon v-if="timePeriodCovered" large color="success">mdi-check-circle</v-icon>
              <v-icon v-else large color="error">mdi-close-circle</v-icon>
              <span class="font-weight-bold ml-1">All Time Periods Covered</span>
            </div>
          </div>
          <div class="col-md-6 pa-0">
            <div>
              <v-icon v-if="locationAndActivityChangeValid" large color="success">mdi-check-circle</v-icon>
              <v-icon v-else large color="error">mdi-close-circle</v-icon>
              <span class="font-weight-bold ml-1">Location Changes Have Relevant Activities</span>
            </div>
            <div>
              <v-icon v-if="areAllBulkTransfersWithinActivityWindow" large color="success">mdi-check-circle</v-icon>
              <v-icon v-else large color="error">mdi-close-circle</v-icon>
              <span class="font-weight-bold ml-1">All Bulk Transfers Within Activity Start / End</span>
            </div>
            <div>
              <v-icon v-if="allResultantTankStatusesWithinRange" large color="success">mdi-check-circle</v-icon>
              <v-icon v-else large color="error">mdi-close-circle</v-icon>
              <span class="font-weight-bold ml-1">All Tank Statuses Are Valid</span>
            </div>
          </div>          
        </v-card>
      </template>
      <template v-slot:item.startDateTimeLt="{ item }">
        <span>
          {{ item.startDateTimeLt | removeTAndSecondsAndFormatDate }}
        </span>
      </template>
      <template v-slot:item.endDateTimeLt="{ item }">
        <span>
          {{ item.endDateTimeLt | removeTAndSecondsAndFormatDate }}
        </span>
      </template>
      <template v-slot:item.issueMessage="{ item }">
        <span class="font-weight-bold">
          {{ item.issueMessage }}
        </span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import DownloadHelperMixin from '@/mixins/DownloadHelperMixin';
import ResponsiveSizeHelperMixin from '@/mixins/ResponsiveSizeHelperMixin';
import ExcelDownloadButton from '../ui/ExcelDownloadButton.vue';

export default {
  name: 'ValidationReport',
  components: { ExcelDownloadButton },
  mixins: [DownloadHelperMixin, ResponsiveSizeHelperMixin],
  data() {
    return {
      headers: [
        { text: 'Start (LT)', value: 'startDateTimeLt' },
        { text: 'End (LT)', value: 'endDateTimeLt' },
        { text: 'Code', value: 'code' },
        { text: 'Activity Type', value: 'activityType' },
        { text: 'Location', value: 'location.name' },
        { text: 'Issue', value: 'issueMessage' },
      ],
      isExportingExcel: false,
    };
  },
  computed: {
    ...mapState(['voyageReportsStore']),
    ...mapGetters({ getVoyageValidationReport: 'voyageReportsStore/getVoyageValidationReport' }),
    voyageReport() {
      return this.voyageReportsStore ? this.voyageReportsStore.voyageReportDetailsData : {};
    },
    voyageStartTimeValid() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.voyageStartTimeValid : null;
    },
    noActivityTimeGaps() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.noActivityTimeGaps : null;
    },
    timePeriodCovered() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.timePeriodCovered : null;
    },
    locationAndActivityChangeValid() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.locationAndActivityChangeValid : null;
    },
    areAllBulkTransfersWithinActivityWindow() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.areAllBulkTransfersWithinActivityWindow : null;
    },
    allResultantTankStatusesWithinRange() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.allResultantTankStatusesWithinRange : null;
    },
    invalidActivities() {
      return this.getVoyageValidationReport
        ? this.getVoyageValidationReport.invalidActivities : [];
    },
    isValidationIssues() {
      return this.getVoyageValidationReport
        && this.getVoyageValidationReport.invalidActivities.length > 0;
    },
  },
  methods: {
    exportAsExcel() {
      this.isExportingExcel = true;
      this.downloadFile('voyageReportsStore/getValidationReportAsExcelFile', this.voyageReport.id)
        .then(() => { this.isExportingExcel = false; });
    },
  },
};
</script>

<style>
</style>
